// Imports => React
import { useEffect, useMemo, useState } from "react";
import { withStore } from "@stores";
import { observer } from "mobx-react-lite";
import clsx from "clsx";

// Imports => Constants
import {
  AcButton,
  AcEmptyBlock,
  AcGetHash,
  AcIcon,
  AcImage,
  AcIsString,
  AcTabContainer,
  THEMES,
} from "@opp/ui-kit";

// Imports => Utilities
import { AcIsSet } from "@opp/ui-kit";

// Imports => Atoms
import { AcRow, AcColumn, AcHeading, AcCard } from "@opp/ui-kit";
import AcGeneralInfo from "./general-info";
import AcProfile from "./profile";
import { useTranslation, withTranslation } from "react-i18next";
import KYCBanner from "@assets/images/kyc_banner.png";
import { AcFormatCurrencyAmount } from "@src/utilities";

const _CLASSES = {
  MAIN: "ac-profile-details-tab ac-profile__grid-container",
};

const AcProfileDetailsTab = ({
  data,
  store: { profile, balance, compliance_requirements, settings },
  t,
}) => {
  const [profileUid, setProfileUid] = useState(false);
  const { balance: balances } = balance;
  const isOnlyRightToWorkUnverified = useMemo(() => {
    const unverified = compliance_requirements.compliance_requirements?.filter(
      (e) => {
        return e.status === "unverified";
      }
    );
    const unverifiedRTW =
      compliance_requirements.compliance_requirements?.filter((e) => {
        return e.status === "unverified" && e.type === "right_to_work.required";
      });
    return unverified?.length && (unverified?.length <= unverifiedRTW?.length);
  }, [compliance_requirements.compliance_requirements]);
  useEffect(() => {
    const hash = AcGetHash();
    if (hash.startsWith("prf_") && hash !== profileUid) {
      setProfileUid(hash);
    }
    if (
      !hash.startsWith("prf_") &&
      AcIsSet(profile.profiles) &&
      profile.profiles.length > 0
    ) {
      setProfileUid(profile.profiles[0].uid);
    }
  }, [profile.profiles]);
  useEffect(() => {
    if (
      AcIsSet(profileUid) &&
      AcIsString(profileUid) &&
      profileUid.startsWith("prf_")
    ) {
      balance.get_profile(t, profileUid);
    }
  }, [profileUid]);
  useEffect(() => {
    if (!AcIsSet(profile.profiles)) {
      profile.list_profiles(t);
    }
  }, [profile.profiles]);
  const {
    compliance_overview_url,
    has_dac7_report,
    change_bank_account_url,
    configurations,
  } = settings;
  const { i18n } = useTranslation();
  const { current_merchant_profiles, current_partner_currency } = profile;

  const getComplianceRequirementText = (object_type) => {
    if (isOnlyRightToWorkUnverified) {
      return `${t("compliance-requirement.right-to-work", {
        ns: "account",
        partner: profile.merchant_profiles.partner.name,
      })}`;
    }
    switch (object_type) {
      case "contact":
        return `${t("compliance-requirement.text-contact", { ns: "account" })}`;
      case "bank_account":
        return `${t("compliance-requirement.text-bank-account", {
          ns: "account",
        })}`;
      default:
        return `${t("compliance-requirement.text-default", { ns: "account" })}`;
    }
  };

  const getComplianceRequirementButtonText = (object_type) => {
    switch (object_type) {
      case "contact":
        return `${t("compliance-requirement.verify-contact", {
          ns: "account",
        })}`;
      case "bank_account":
        return `${t("compliance-requirement.verify-bank-account", {
          ns: "account",
        })}`;
      case "coc":
      case "ubo":
        return `${t("compliance-requirement.verify-company", {
          ns: "account",
        })}`;
      default:
        return `${t("compliance-requirement.verify-default", {
          ns: "account",
        })}`;
    }
  };

  const getComplianceRequirementIcon = (object_type) => {
    let icon = "description-outline";

    switch (object_type) {
      case "contact":
        icon = "account-details-outline";
        break;
      case "bank_account":
        icon = "bank-plus";
        break;
    }

    return icon;
  };

  const renderUnverifiedComplianceRequirements = useMemo(() => {
    let unverifiedComplianceRequirement = null;
    if (!AcIsSet(data)) {
      return null;
    }

    if (configurations.kyc === false) {
      return null;
    }

    if (
      AcIsSet(compliance_requirements) &&
      AcIsSet(
        compliance_requirements.current_unverified_compliance_requirements
      ) &&
      compliance_requirements.current_unverified_compliance_requirements
        .length > 0
    ) {
      unverifiedComplianceRequirement =
        compliance_requirements.current_unverified_compliance_requirements.filter(
          (e) => e.type !== "right_to_work.required"
        )[0];
    } else {
      if (
        !AcIsSet(profile) ||
        !AcIsSet(profile.profiles) ||
        profile.profiles.length === 0
      ) {
        return null;
      }
      profile.profiles.every((profileToCheck) => {
        if (AcIsSet(profileToCheck.bank_account)) {
          const bankAccount = profileToCheck.bank_account;
          if (
            bankAccount.status === "new" ||
            bankAccount.status === "disapproved"
          ) {
            // If there is connected bank account that is not approved we 'fake' and cr to show the card.
            unverifiedComplianceRequirement = {
              object_redirect_url: bankAccount.verification_url
                ? bankAccount.verification_url
                : compliance_overview_url,
              object_type: "bank_account",
            };
            return false;
          }
        }

        return true;
      });
    }

    if (unverifiedComplianceRequirement === null) {
      return null;
    }

    return (
      <>
        <div
          className={
            "ac-profile__column ac-profile__column--half h-margin-bottom-40"
          }
        >
          <AcRow className={"h-position-absolute h-height-100-p"}>
            <AcColumn
              className={"h-flex-h-align-center"}
              xxs={12}
              xs={12}
              md={6}
              lg={6}
            >
              <AcHeading rank={1} className={"ac-profile__kyc-absolute-text"}>
                {t(
                  "compliance-requirement.need-more-info",
                  "We need more information",
                  { ns: "account" }
                )}
              </AcHeading>
            </AcColumn>
          </AcRow>

          <span className={"ac-profile__kyc-inset-badge"} />

          <AcImage
            wrpClassName={"ac-profile__kyc-banner"}
            source={KYCBanner}
            style={{
              cursor: isOnlyRightToWorkUnverified ? "default" : undefined,
            }}
            key={"ac-kyc-banner-image"}
            transparent
            onClick={() => {
              !isOnlyRightToWorkUnverified &&
                window.open(
                  unverifiedComplianceRequirement.object_redirect_url,
                  "_blank",
                  "noopener,noreferrer"
                );
            }}
            title={
              isOnlyRightToWorkUnverified
                ? ""
                : getComplianceRequirementButtonText(
                    unverifiedComplianceRequirement.object_type
                  )
            }
          />

          <div className={"ac-profile__kyc-bottom-piece"}>
            <AcRow className={"h-height-100-p"}>
              <AcColumn
                className={"h-flex-h-align-center"}
                xxs={8}
                xs={8}
                md={8}
                lg={8}
              >
                <span className={"ac-profile__kyc-bottom-piece-text"}>
                  {getComplianceRequirementText(
                    unverifiedComplianceRequirement?.object_type
                  )}
                </span>
              </AcColumn>
              {!isOnlyRightToWorkUnverified && (
                <AcColumn
                  className={"h-flex-h-align-end"}
                  xxs={4}
                  xs={4}
                  md={4}
                  lg={4}
                >
                  <AcButton
                    tag={"a"}
                    target={"_blank"}
                    href={unverifiedComplianceRequirement.object_redirect_url}
                    theme={THEMES.OMEGA}
                    className={"ac-profile__kyc-bottom-piece-button"}
                  >
                    <AcIcon
                      icon={getComplianceRequirementIcon(
                        unverifiedComplianceRequirement.object_type
                      )}
                    />
                    <span>
                      {getComplianceRequirementButtonText(
                        unverifiedComplianceRequirement.object_type
                      )}
                    </span>
                  </AcButton>
                </AcColumn>
              )}
            </AcRow>
          </div>
        </div>

        <AcCard
          className={
            "ac-profile__column ac-profile__column--half h-hidden-xxs h-hidden-xs h-hidden-sm"
          }
          theme={THEMES.TRANSPARENT}
        />
      </>
    );
  }, [
    compliance_requirements,
    profile,
    compliance_overview_url,
    isOnlyRightToWorkUnverified,
    configurations.kyc,
    data,
    getComplianceRequirementButtonText,
    getComplianceRequirementText,
    t,
  ]);

  const renderProfiles = useMemo(() => {
    if (
      !AcIsSet(profile) ||
      !AcIsSet(profile.profiles) ||
      profile.profiles.length === 0
    ) {
      return (
        <>
          <AcEmptyBlock
            message={t("profile.no-profiles-found", "No profiles found", {
              ns: "account",
            })}
          />

          <AcCard
            className={
              "ac-profile__column ac-profile__column--half h-hidden-xxs h-hidden-xs h-hidden-sm"
            }
            theme={THEMES.TRANSPARENT}
          />
        </>
      );
    }

    const collection = profile.profiles.filter(
      (e) => e.status !== "terminated"
    );

    balances?.profile_balances.forEach(({ uid, amount, amount_planned }) => {
      const matchedProfile = collection.find((item) => {
        return item.uid === uid;
      });
      if (matchedProfile) {
        matchedProfile.amount = amount - amount_planned;
      }
    });
    const profileWithBankAmount = collection.findIndex((profile) => {
      return profile.amount;
    });
    if (profileWithBankAmount !== -1) {
      collection.unshift(...collection.splice(profileWithBankAmount, 1));
    }
    const len = collection.length;
    let n = 0;
    let result = [];

    for (n; n < len; n++) {
      const profile = collection[n];

      const object = {
        id: profile.uid,
        label: `${profile.name}${
          profile.po_number ? ` - ${profile.po_number}` : ""
        }(${AcFormatCurrencyAmount(
          profile.amount,
          current_partner_currency,
          i18n.resolvedLanguage,
          true
        )})`,
        disabled: false,
        component: (
          <AcProfile
            profile={profile}
            complianceOverviewUrl={compliance_overview_url}
          />
        ),
        allowed: null,
      };

      result.push(object);
    }

    return (
      <>
        <AcCard
          className={
            "ac-profile__column ac-profile__column--wide h-padding-x-0 h-padding-bottom-0 h-margin-bottom-0"
          }
          theme={THEMES.TRANSPARENT}
        >
          <AcHeading theme={THEMES.DARK} rank={1} className={"h-margin-y-20"}>
            {t("profile.your-profiles", "Your profiles", {
              count: result.length,
              ns: "account",
            })}
          </AcHeading>

          <AcRow>
            <AcColumn>
              <AcTabContainer
                disableIndicator={true}
                collection={result}
                data={null}
                onlyDropdown={true}
              />
            </AcColumn>
          </AcRow>
        </AcCard>
      </>
    );
  }, [profile, profile.profiles]);

  const getMainClassNames = useMemo(() => {
    return clsx(_CLASSES.MAIN);
  });

  const getCurrentMerchant = useMemo(() => {
    if (
      !AcIsSet(current_merchant_profiles) ||
      !AcIsSet(current_merchant_profiles.merchant)
    ) {
      return;
    }

    return current_merchant_profiles.merchant;
  }, [current_merchant_profiles]);

  return (
    <div className={getMainClassNames}>
      {renderUnverifiedComplianceRequirements}

      <AcCard className={"ac-profile__column ac-profile__column--half"}>
        <AcGeneralInfo
          merchant={getCurrentMerchant}
          complianceOverviewUrl={compliance_overview_url}
          hasDac7Report={has_dac7_report}
          changeBankAccountUrl={change_bank_account_url}
          complianceRequirements={
            compliance_requirements.current_compliance_requirements
          }
          configurations_kyc={configurations.kyc}
        />
      </AcCard>

      <AcCard
        className={
          "ac-profile__column ac-profile__column--half h-hidden-xxs h-hidden-xs h-hidden-sm"
        }
        theme={THEMES.TRANSPARENT}
      />

      {renderProfiles}
    </div>
  );
};

export default withTranslation(["account"])(
  withStore(observer(AcProfileDetailsTab))
);
